import { useCanvas } from '@components/ArtBoard/Canvas/hooks/events/useCanvas'
import { NEW_THREAD_LAYER_ID } from '@components/ArtBoard/Threads'
import type { ArtBoardCursorMode } from '@store/reducers/artboard/types'
import type { FullGestureState } from '@use-gesture/react'
import { type Dispatch, type RefObject, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GlobalActions } from '@/store'
import type { AppState } from '@/types'
import type { Rectangle } from '@/utils/math'
import { delta } from '../../algorithms'
import { POINTER_MULTIPLIER } from '../../constants'
import { getCanvasState } from '../../helpers/canvas'
import useMomentum from '../useMomentum'

export const ELEMENT_GESTURE_LISTENER_CONTAINER = 'gestureListenerContainer'
export const ELEMENT_GESTURE_MOVABLE_CONTAINER = 'gestureMovableContainer'

type Event = Omit<FullGestureState<'drag'>, 'event'> & {
  event: PointerEvent | MouseEvent | TouchEvent | KeyboardEvent
}

type UsePointerEvents = {
  onDragStart: (event: Event, ref: RefObject<any>) => void
  onDrag: (event: Event) => void
  onDragEnd: (event: Event, ref: RefObject<any>) => void
}

type State = {
  mouse: number[]
  offset: number[]
}

const useDragEvents = (
  onChangePointerSelection: Dispatch<Rectangle | null> | undefined
): UsePointerEvents => {
  const cursorMode = useSelector((store: AppState) => store.artboard.cursorMode)
  const cursorModeRef = useRef<ArtBoardCursorMode>(cursorMode)
  const dispatch = useDispatch()

  const [state, setState] = useState<State | null>(null)
  const lastMousePositionsRef = useRef<number[][]>([])

  const momentum = useMomentum()
  const dispatchCanvasUpdate = useCanvas()

  const onDragStart = (event: Event, ref: RefObject<any>) => {
    const target = event.target as HTMLDivElement
    // over the comment layer the left button always pans: there is nothing
    // to rubber-band select while placing a comment
    cursorModeRef.current =
      target.id === NEW_THREAD_LAYER_ID ? 'grab' : cursorMode

    momentum.cancel()
    dispatch(GlobalActions.cancelJump())
    lastMousePositionsRef.current = []

    if (
      [
        ELEMENT_GESTURE_LISTENER_CONTAINER,
        ELEMENT_GESTURE_MOVABLE_CONTAINER,
        NEW_THREAD_LAYER_ID,
      ].includes(target.id) &&
      ref?.current
    ) {
      const { translate } = getCanvasState(ref.current)
      //
      const mouseX = event.xy[0]
      const mouseY = event.xy[1]
      const offsetX = translate[0]
      const offsetY = translate[1]

      setState({
        mouse: [mouseX, mouseY],
        offset: [offsetX, offsetY],
      })
    }
  }

  const onDrag = (event: Event) => {
    if (!state) {
      return
    }

    const isMiddleMousePressed = event.buttons === 4
    const isLeftMousePressed = event.buttons === 1

    if (
      isMiddleMousePressed ||
      (isLeftMousePressed && cursorModeRef.current !== 'select')
    ) {
      const { dx, dy } = delta({
        x0: state.mouse[0],
        y0: state.mouse[1],
        x1: event.xy[0],
        y1: event.xy[1],
      })

      lastMousePositionsRef.current.push(event.xy)
      if (lastMousePositionsRef.current.length > 3) {
        lastMousePositionsRef.current.shift()
      }

      const tx = state.offset[0] + dx * POINTER_MULTIPLIER
      const ty = state.offset[1] + dy * POINTER_MULTIPLIER

      dispatchCanvasUpdate({ translate: [tx, ty] })
    }

    if (
      isLeftMousePressed &&
      cursorModeRef.current === 'select' &&
      onChangePointerSelection
    ) {
      onChangePointerSelection({
        x: Math.min(state.mouse[0], event.xy[0]),
        y: Math.min(state.mouse[1], event.xy[1]),
        width: Math.abs(state.mouse[0] - event.xy[0]),
        height: Math.abs(state.mouse[1] - event.xy[1]),
      })
    }
  }

  const onDragEnd = async (event: Event, ref: RefObject<any>) => {
    setState(null)

    if (cursorModeRef.current === 'select' && onChangePointerSelection) {
      setTimeout(() => {
        // Pointer selection must work even with 0 time, that's why we use setTimeout
        onChangePointerSelection(null)
      })
      return
    }
    if (cursorModeRef.current === 'grab' && state && ref?.current) {
      const { translate } = getCanvasState(ref.current)

      // Жест мог закончиться, не дав ни одного onDrag — тогда позиций нет
      // вовсе. Так приходит клик без движения по тому, что лежит над холстом:
      // до OCT-5222 такого не случалось, потому что меню в тулбаре стрелки
      // не было. Чтение [0][0] на пустом массиве роняло весь артборд.
      const [firstPosition = [0, 0]] = lastMousePositionsRef.current

      const { dx, dy } = delta({
        x0: firstPosition[0] || 0,
        y0: firstPosition[1] || 0,
        x1: event.xy[0],
        y1: event.xy[1],
      })
      momentum.begin({
        ref: ref.current,
        velocity: event.velocity,
        dxy: [dx, dy],
        translate,
      })
    }
  }

  return {
    onDragStart,
    onDrag,
    onDragEnd,
  }
}

export default useDragEvents
